import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View, } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
// Common utilities
import { useFlatListRenderItem } from '../utils/commonHooks';
// Common components
import { Alert } from '../components/Alert';
import Avatar from './Avatar';
import { Button } from './Button';
import { ClientActionsMenu } from './ClientActionsMenu';
import { ClientNameList } from './ClientNameList';
import { ActivationDialog, DeactivationDialog } from '../activationDialog';
// Data model
import { selectedUserIdSelector, setSelectedUserId } from '../redux/appSlice';
import { useDeleteUserMutation, useDismissAccountDeletionRequestMutation, useGetUserQuery, useGetUsersQuery, useRestoreUserMutation, useUpdateUserMutation, } from '../services/abbiWayApi';
import { theme } from '../theme';
import { WeekStartsOnControl } from './WeekStartsOnControl';
import { reportServerError } from '../utils/reportServerError';
import { format } from 'date-fns';
import { Tooltip } from 'react-native-paper';
import { formatReadableDateTime } from '../utils/date';
import { clientListGroup } from '../crm/clientListGroup';
function isProcessedAccountDeletion(user) {
    return (!!user.processedAccountDeletion ||
        (user.email == null &&
            user.firstName === 'Deleted' &&
            user.lastName === 'User'));
}
/** Soft-delete / process time for CRM labels. */
function accountDeletionAt(user) {
    const raw = user.processedAccountDeletion?.processedAt || user.deletedAt || null;
    if (!raw) {
        return null;
    }
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
}
function formatDeletedShort(date) {
    return format(date, 'MMM d, yyyy');
}
export const UserInfo = ({ user, onAddClientPress, onGenerateProgramPress, phone = false, onClientPress, }) => {
    const selectedUserId = useSelector(selectedUserIdSelector);
    // GET /users/:id is admin-only; signed-in clients use `user` (me) instead of that endpoint.
    const skipSelectedUserFetch = !selectedUserId ||
        (user.role.name !== 'Admin' && selectedUserId === user.id);
    const { data: selectedUserFromApi } = useGetUserQuery(selectedUserId, {
        skip: skipSelectedUserFetch,
    });
    const selectedUserInfo = user.role.name !== 'Admin' && selectedUserId === user.id
        ? user
        : selectedUserFromApi;
    const isAdminUser = user.role.name === 'Admin';
    const canManageClients = isAdminUser;
    const { data: users } = useGetUsersQuery({}, {
        refetchOnMountOrArgChange: true,
        skip: !isAdminUser,
    });
    const { data: archivedUsersResponse } = useGetUsersQuery({
        includeArchived: true,
    }, {
        refetchOnMountOrArgChange: true,
        skip: !isAdminUser,
    });
    const [search, setSearch] = useState('');
    const [showProspects, setShowProspects] = useState(false);
    const [showCompleted, setShowCompleted] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [activateDialogVisible, setActivateDialogVisible] = useState(false);
    const [deactivateDialogVisible, setDeactivateDialogVisible] = useState(false);
    const [menuVisible, setMenuVisible] = useState(false);
    const [deleteUser] = useDeleteUserMutation();
    const [restoreUser, { isLoading: isRestoring }] = useRestoreUserMutation();
    const [updateUser, { isLoading: isSavingWeekStart }] = useUpdateUserMutation();
    const [dismissDeletionRequest] = useDismissAccountDeletionRequestMutation();
    const pendingDeletion = selectedUserInfo?.pendingDeletionRequest;
    const processedDeletion = selectedUserInfo
        ? isProcessedAccountDeletion(selectedUserInfo)
        : false;
    const selectedDeletedAt = selectedUserInfo
        ? accountDeletionAt(selectedUserInfo)
        : null;
    const dispatch = useDispatch();
    const onPress = useCallback((thisUser) => {
        dispatch(setSelectedUserId(thisUser.id));
        onClientPress?.(thisUser);
    }, [dispatch, onClientPress]);
    const onClientWeekStartsOnChange = useCallback(async (weekStartsOn) => {
        if (!selectedUserInfo?.id || selectedUserInfo.weekStartsOn === weekStartsOn) {
            return;
        }
        try {
            await updateUser({
                userId: selectedUserInfo.id,
                payload: { weekStartsOn },
            }).unwrap();
        }
        catch (error) {
            reportServerError(error);
        }
    }, [selectedUserInfo?.id, selectedUserInfo?.weekStartsOn, updateUser]);
    const renderUserItem = useFlatListRenderItem(({ item }) => {
        const selected = item.id === selectedUserInfo?.id;
        return (<TouchableOpacity style={selected ? styles.selectedUser : undefined} onPress={() => onPress(item)} key={item.id}>
          <Text style={styles.username}>
            {`${item.firstName} ${item.lastName}`}
            {item.pendingDeletionRequest ? ' — deletion requested' : ''}
          </Text>
        </TouchableOpacity>);
    }, [selectedUserInfo]);
    const renderArchivedUserItem = useFlatListRenderItem(({ item }) => {
        const itemProcessedDeletion = isProcessedAccountDeletion(item);
        const deletedAt = accountDeletionAt(item);
        const shortDeleted = deletedAt ? formatDeletedShort(deletedAt) : null;
        const fullDeleted = deletedAt
            ? formatReadableDateTime(deletedAt)
            : null;
        const label = itemProcessedDeletion
            ? shortDeleted
                ? `Deleted User — ${shortDeleted}`
                : 'Deleted User — account deleted'
            : `${item.firstName} ${item.lastName}`;
        const nameText = (<Text style={styles.username} accessibilityLabel={itemProcessedDeletion && fullDeleted
                ? `Deleted User, deleted ${fullDeleted}`
                : label}>
          {label}
        </Text>);
        return (<View style={styles.archivedUserItem}>
          <TouchableOpacity style={styles.archivedUserRow} onPress={() => onPress(item)}>
            {itemProcessedDeletion && fullDeleted ? (<Tooltip title={`Deleted ${fullDeleted}`}>
                <View>{nameText}</View>
              </Tooltip>) : (nameText)}
          </TouchableOpacity>
          {!itemProcessedDeletion ? (<Button title="Unarchive" style={styles.smallButton} buttonStyle={styles.unarchiveButton} loading={isRestoring} onPress={() => unarchiveUserPressed(item)}/>) : null}
        </View>);
    }, [isRestoring]);
    const activated = selectedUserInfo?.programType !== null;
    const addUserPressed = () => {
        if (onAddClientPress) {
            onAddClientPress();
        }
        else {
            console.warn('UserInfo: onAddClientPress callback is not provided.');
        }
    };
    const archiveUserPressed = () => {
        if (!selectedUserInfo) {
            return;
        }
        Alert('Archive Client', `Are you sure you want to archive ${selectedUserInfo.firstName} ${selectedUserInfo.lastName}? They will be hidden from the client list but their data will be preserved.`, [
            {
                text: 'Archive',
                style: 'default',
                onPress: () => {
                    deleteUser({ userId: selectedUserInfo.id, permanent: false })
                        .then(() => {
                        if (users) {
                            dispatch(setSelectedUserId(users?.data[0].id));
                        }
                        else {
                            dispatch(setSelectedUserId(undefined));
                        }
                    })
                        .catch(() => {
                        // TODO: display error message
                    });
                },
            },
            { text: 'Cancel', style: 'cancel' },
        ]);
    };
    const dismissDeletionPressed = () => {
        if (!selectedUserInfo) {
            return;
        }
        Alert('Dismiss deletion request', `This cancels the pending deletion request for ${selectedUserInfo.firstName} ${selectedUserInfo.lastName}. Their account is unchanged. They can request deletion again later.`, [
            {
                text: 'Dismiss request',
                style: 'default',
                onPress: () => {
                    dismissDeletionRequest(selectedUserInfo.id)
                        .unwrap()
                        .catch(() => {
                        Alert('Could not dismiss request', 'Something went wrong dismissing the deletion request. Please try again.', [{ text: 'OK', style: 'default' }]);
                    });
                },
            },
            { text: 'Cancel', style: 'cancel' },
        ]);
    };
    const deleteForeverPressed = () => {
        if (!selectedUserInfo) {
            return;
        }
        // D2.5: admin delete = partial PII removal (always available)
        Alert('Process account deletion', `This removes personal info for ${selectedUserInfo.firstName} ${selectedUserInfo.lastName} (name, email, phone, photos), anonymizes notes/intake, and frees their email for re-signup. Meal and weigh-in history is kept de-identified for training. This is not the same as Archive.\n\nContinue?`, [
            {
                text: 'Process deletion',
                style: 'destructive',
                onPress: () => {
                    deleteUser({ userId: selectedUserInfo.id, permanent: true })
                        .then(() => {
                        if (users) {
                            dispatch(setSelectedUserId(users?.data[0].id));
                        }
                        else {
                            dispatch(setSelectedUserId(undefined));
                        }
                    })
                        .catch(() => {
                        // TODO: display error message
                    });
                },
            },
            { text: 'Cancel', style: 'cancel' },
        ]);
    };
    const { currentClients, prospectClients, completedClients } = useMemo(() => {
        const byName = (a, b) => {
            const lastNameCompare = (a.lastName || '')
                .toLowerCase()
                .localeCompare((b.lastName || '').toLowerCase());
            if (lastNameCompare !== 0) {
                return lastNameCompare;
            }
            return (a.firstName || '')
                .toLowerCase()
                .localeCompare((b.firstName || '').toLowerCase());
        };
        const current = [];
        const prospects = [];
        const completed = [];
        if (!users?.data) {
            return {
                currentClients: current,
                prospectClients: prospects,
                completedClients: completed,
            };
        }
        const seen = new Set();
        for (const theUser of users.data) {
            if (theUser.deletedAt || seen.has(theUser.id)) {
                continue;
            }
            seen.add(theUser.id);
            if (search) {
                const fullName = `${theUser.firstName} ${theUser.lastName}`.toLowerCase();
                if (!fullName.includes(search.toLowerCase())) {
                    continue;
                }
            }
            const group = clientListGroup(theUser);
            if (group === 'current') {
                current.push(theUser);
            }
            else if (group === 'prospect') {
                prospects.push(theUser);
            }
            else if (group === 'completed') {
                completed.push(theUser);
            }
        }
        current.sort(byName);
        prospects.sort(byName);
        completed.sort(byName);
        return {
            currentClients: current,
            prospectClients: prospects,
            completedClients: completed,
        };
    }, [users, search]);
    const archivedUsers = useMemo(() => {
        if (!archivedUsersResponse?.data) {
            return [];
        }
        // Filter to only show archived users
        const archived = archivedUsersResponse.data.filter(theUser => theUser.deletedAt);
        const filteredArchived = archived.filter(theUser => {
            if (!search) {
                return true;
            }
            const fullName = `${theUser.firstName} ${theUser.lastName}`.toLowerCase();
            return fullName.includes(search.toLowerCase());
        });
        return filteredArchived.sort((a, b) => {
            const aLastName = (a.lastName || '').toLowerCase();
            const bLastName = (b.lastName || '').toLowerCase();
            const lastNameCompare = aLastName.localeCompare(bLastName);
            if (lastNameCompare !== 0) {
                return lastNameCompare;
            }
            const aFirstName = (a.firstName || '').toLowerCase();
            const bFirstName = (b.firstName || '').toLowerCase();
            const firstNameCompare = aFirstName.localeCompare(bFirstName);
            if (firstNameCompare !== 0) {
                return firstNameCompare;
            }
            // Same display name (e.g. multiple Deleted Users): newest deletion first
            const aAt = accountDeletionAt(a)?.getTime() ?? 0;
            const bAt = accountDeletionAt(b)?.getTime() ?? 0;
            return bAt - aAt;
        });
    }, [archivedUsersResponse, search]);
    const unarchiveUserPressed = (archivedUser) => {
        if (isProcessedAccountDeletion(archivedUser)) {
            Alert('Cannot Unarchive', 'This client was processed for account deletion (personal info removed). Unarchive is not allowed — if they return, have them sign up again.', [{ text: 'OK', style: 'default' }]);
            return;
        }
        Alert('Unarchive Client', `Are you sure you want to unarchive ${archivedUser.firstName} ${archivedUser.lastName}? They will be restored to the active client list.`, [
            {
                text: 'Unarchive',
                style: 'default',
                onPress: () => {
                    restoreUser(archivedUser.id)
                        .then(() => {
                        // Optionally select the restored user
                        dispatch(setSelectedUserId(archivedUser.id));
                    })
                        .catch(() => {
                        Alert('Error', 'Failed to unarchive client. Please try again.', [{ text: 'OK', style: 'default' }]);
                    });
                },
            },
            { text: 'Cancel', style: 'cancel' },
        ]);
    };
    return (<View style={[styles.content, phone && styles.contentPhone]}>
      {!phone && selectedUserInfo?.firstName && selectedUserInfo?.lastName && (<View style={styles.selectedClientHeader}>
          <View style={styles.avatar}>
            <Avatar firstName={selectedUserInfo.firstName} lastName={selectedUserInfo.lastName}/>
            <Text style={styles.userName}>
              {`${selectedUserInfo.firstName} ${selectedUserInfo.lastName}`}
              {isAdminUser && (<Text style={styles.userId}> (ID: {selectedUserInfo.id})</Text>)}
            </Text>
            {pendingDeletion ? (<Text style={styles.deletionRequested}>
                Deletion requested — follow up; do not auto-wipe. Dismiss from
                the menu if this was accidental.
              </Text>) : null}
            {processedDeletion ? (<Text style={styles.deletionRequested}>
                {`Account deletion processed${selectedDeletedAt
                    ? ` ${formatReadableDateTime(selectedDeletedAt)}`
                    : ''} — PII removed. Unarchive is blocked; meals/weigh-ins stay de-identified under this user id for training. Returning clients sign up again.`}
              </Text>) : null}
          </View>
          {canManageClients &&
                selectedUserInfo.role.name !== 'Admin' &&
                !processedDeletion && (<TouchableOpacity style={styles.menuButton} onPress={() => setMenuVisible(true)}>
              <Text style={styles.menuIcon}>⋮</Text>
            </TouchableOpacity>)}
        </View>)}
      {!phone &&
            selectedUserInfo &&
            selectedUserInfo.role.name !== 'Admin' &&
            activated && (<View style={styles.details}>
            <Text style={styles.detailsText}>
              {`Program type: ${selectedUserInfo.programType}`}
            </Text>
            {canManageClients ? (<WeekStartsOnControl compact value={selectedUserInfo.weekStartsOn} onChange={onClientWeekStartsOnChange} isSaving={isSavingWeekStart}/>) : selectedUserInfo.weekStartsOn === 0 ||
                selectedUserInfo.weekStartsOn === 1 ? (<Text style={styles.detailsText}>
                {`Week starts: ${selectedUserInfo.weekStartsOn === 0 ? 'Sunday' : 'Monday'}`}
              </Text>) : null}
          </View>)}
      {!phone &&
            canManageClients &&
            selectedUserInfo &&
            selectedUserInfo.role.name !== 'Admin' && (<ClientActionsMenu visible={menuVisible} onClose={() => setMenuVisible(false)} items={processedDeletion
                ? []
                : [
                    {
                        label: activated ? 'Complete' : 'Activate',
                        onPress: () => activated
                            ? setDeactivateDialogVisible(true)
                            : setActivateDialogVisible(true),
                    },
                    {
                        label: 'Archive Client',
                        onPress: archiveUserPressed,
                    },
                    ...(pendingDeletion
                        ? [
                            {
                                label: 'Dismiss deletion request',
                                onPress: dismissDeletionPressed,
                            },
                        ]
                        : []),
                    {
                        label: 'Process account deletion',
                        onPress: deleteForeverPressed,
                        danger: true,
                    },
                ]}/>)}
      {isAdminUser && !phone && (<>
          <Button title="Add Client" style={styles.button} onPress={addUserPressed}/>
          {onGenerateProgramPress && (<Button title="Generate program" style={styles.button} onPress={onGenerateProgramPress}/>)}
        </>)}
      {isAdminUser && users?.data ? (<View style={phone ? undefined : styles.clientManagementContainer}>
          <View style={styles.activeSection}>
            <View style={styles.searchContainer}>
              <TextInput style={styles.searchInput} placeholder="Search clients" onChangeText={setSearch} value={search} clearButtonMode="while-editing"/>
            </View>
            <Text style={styles.sectionHeader}>Current Clients</Text>
            <ClientNameList data={currentClients} renderItem={renderUserItem} keyExtractor={item => `current-${item.id}`} bounded={!phone} frameStyle={[
                styles.userListCard,
                !phone && styles.userListClip,
                !phone && styles.userList,
            ]} emptyLabel="No current clients"/>
          </View>
          {prospectClients.length > 0 && (<Button title={showProspects
                    ? 'Hide Prospects'
                    : `Show Prospects (${prospectClients.length})`} style={styles.toggleButton} buttonStyle={styles.toggleButtonStyle} onPress={() => setShowProspects(!showProspects)}/>)}
          {showProspects && prospectClients.length > 0 && (<View style={styles.archivedSection}>
              <Text style={styles.sectionHeader}>Prospects</Text>
              <ClientNameList data={prospectClients} renderItem={renderUserItem} keyExtractor={item => `prospect-${item.id}`} bounded={!phone} frameStyle={[
                    styles.userListCard,
                    !phone && styles.userListClip,
                    !phone && styles.archivedUserList,
                ]}/>
            </View>)}
          {completedClients.length > 0 && (<Button title={showCompleted
                    ? 'Hide Completed'
                    : `Show Completed (${completedClients.length})`} style={styles.toggleButton} buttonStyle={styles.toggleButtonStyle} onPress={() => setShowCompleted(!showCompleted)}/>)}
          {showCompleted && completedClients.length > 0 && (<View style={styles.archivedSection}>
              <Text style={styles.sectionHeader}>Completed</Text>
              <ClientNameList data={completedClients} renderItem={renderUserItem} keyExtractor={item => `completed-${item.id}`} bounded={!phone} frameStyle={[
                    styles.userListCard,
                    !phone && styles.userListClip,
                    !phone && styles.archivedUserList,
                ]}/>
            </View>)}
          {!phone && archivedUsers.length > 0 && (<Button title={showArchived
                    ? 'Hide Archived'
                    : `Show Archived (${archivedUsers.length})`} style={styles.toggleButton} buttonStyle={styles.toggleButtonStyle} onPress={() => setShowArchived(!showArchived)}/>)}
          {!phone && showArchived && archivedUsers.length > 0 && (<View style={styles.archivedSection}>
              <Text style={styles.sectionHeader}>Archived Clients</Text>
              <ClientNameList data={archivedUsers} renderItem={renderArchivedUserItem} keyExtractor={item => `archived-${item.id}`} bounded frameStyle={[
                    styles.userListCard,
                    styles.userListClip,
                    styles.archivedUserList,
                ]}/>
            </View>)}
        </View>) : null}
      {activateDialogVisible && (<ActivationDialog user={selectedUserInfo} me={user} close={() => setActivateDialogVisible(false)}/>)}
      {deactivateDialogVisible && (<DeactivationDialog user={selectedUserInfo} me={user} close={() => setDeactivateDialogVisible(false)}/>)}
    </View>);
};
const styles = StyleSheet.create({
    content: {
        marginTop: 32,
        marginBottom: 32,
    },
    contentPhone: {
        marginTop: 8,
        marginBottom: 16,
    },
    selectedClientHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    avatar: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    menuButton: {
        padding: 8,
        marginLeft: 8,
        justifyContent: 'center',
        alignItems: 'center',
    },
    menuIcon: {
        color: theme.colors.surfaceElevated,
        fontSize: 24,
        fontWeight: 'bold',
        lineHeight: 24,
    },
    userName: {
        color: theme.colors.surfaceElevated,
        marginTop: 10,
        fontSize: 22,
    },
    userId: {
        fontSize: 16,
        opacity: 0.85,
    },
    deletionRequested: {
        // Light callout on sidePanelBg (#7780AF) — plain danger red fails contrast there
        color: theme.colors.dangerText,
        backgroundColor: theme.colors.dangerBackground,
        marginTop: 10,
        marginHorizontal: 8,
        fontSize: 13,
        lineHeight: 18,
        fontWeight: '600',
        textAlign: 'center',
        paddingVertical: 8,
        paddingHorizontal: 10,
        borderRadius: 8,
        overflow: 'hidden',
    },
    details: {
        marginTop: 16,
        marginBottom: 32,
    },
    detailsText: {
        color: theme.colors.surfaceElevated,
        fontSize: 17,
        marginVertical: 10,
    },
    searchContainer: {
        borderWidth: 1,
        borderRadius: 16,
        borderColor: theme.colors.controlInactive,
        backgroundColor: theme.colors.surfaceElevated,
    },
    searchInput: {
        padding: 10,
        fontSize: 16,
    },
    clientManagementContainer: {
        flex: 1,
    },
    archivedSection: {
        marginBottom: 20,
    },
    activeSection: {
        marginBottom: 20,
    },
    toggleButton: {
        marginTop: 16,
    },
    toggleButtonStyle: {
        borderWidth: 0,
        elevation: 0,
    },
    userListCard: {
        marginTop: 8,
        backgroundColor: theme.colors.surfaceElevated,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.borderSubtle,
    },
    // Desktop boxes clip to the rounded cap. Phone lists leave this off so a
    // touch can scroll the page (overflow:hidden around the rows blocks that
    // on iOS Safari).
    userListClip: {
        overflow: 'hidden',
    },
    userList: {
        maxHeight: 220,
    },
    archivedUserList: {
        maxHeight: 190,
    },
    username: {
        fontSize: 16,
        padding: 5,
    },
    selectedUser: {
        backgroundColor: theme.colors.controlInactive,
    },
    button: {
        marginTop: 8,
        marginBottom: 8,
    },
    dangerButton: {
        backgroundColor: theme.colors.danger,
    },
    sectionHeader: {
        color: theme.colors.surfaceElevated,
        fontSize: 16,
        fontWeight: 'bold',
        marginTop: 16,
        marginBottom: 8,
    },
    archivedUserItem: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 4,
        paddingHorizontal: 8,
        paddingRight: 16,
    },
    archivedUserRow: {
        flex: 1,
    },
    smallButton: {
        marginTop: 4,
        marginBottom: 4,
        marginLeft: 8,
    },
    unarchiveButton: {
        backgroundColor: theme.colors.success,
        borderWidth: 0,
        borderColor: theme.colors.transparent,
        elevation: 0,
    },
});
